<template>
  <div class="dp-page py-3">
    <header class="dp-toolbar">
      <div class="dp-toolbar__title">
        <span class="dp-toolbar__accent" aria-hidden="true"></span>
        <div>
          <h1>Discount Presets</h1>
          <p>Per-organisation quantity tiers that drive the eligible-discount toggle on the order form.</p>
        </div>
      </div>
      <div class="dp-toolbar__actions">
        <CButton color="primary" size="sm" :disabled="!selectedOrgId" @click="openCreateModal">+ Add Tier</CButton>
        <CButton
          size="sm"
          class="bulk-btn"
          :disabled="!selectedOrgId || !items.length"
          @click="openBulkModal"
        >Bulk Apply…</CButton>
        <CButton
          size="sm"
          class="refresh-btn"
          :class="{ 'is-loading': isLoading }"
          :disabled="isLoading"
          @click="loadPresets"
        >
          {{ isLoading ? 'Refreshing…' : 'Refresh' }}
        </CButton>
      </div>
    </header>

    <CAlert v-if="errorMessage" color="danger" show class="dp-alert">{{ errorMessage }}</CAlert>
    <CAlert v-if="successMessage" color="success" show class="dp-alert">{{ successMessage }}</CAlert>

    <section class="dp-section">
      <div class="dp-section__body">
        <div class="dp-filters">
          <div class="dp-filter">
            <label class="dp-label">Organisation</label>
            <AppDropdown
              v-model="selectedOrgId"
              :options="orgDropdownOptions"
              placeholder="Select organisation"
            />
          </div>
          <div class="dp-filter dp-filter--hint">
            <label class="dp-label" aria-hidden="true">&nbsp;</label>
            <small class="dp-hint">Tier matching uses the highest <em>Min Quantity</em> the order meets.</small>
          </div>
        </div>
      </div>
    </section>

    <BaseTable
      title="Tiers"
      :items="tableRows"
      :columns="columns"
      :loading="isLoading"
      :sortable="false"
      empty-text="No tiers configured for this organisation yet."
    >
      <template #cell-group_key="{ row }">
        <span v-if="row.group_key" class="dp-pill dp-pill--violet">{{ row.group_key }}</span>
        <span v-else class="dp-sub">—</span>
      </template>
      <template #cell-mode="{ row }">
        <span :class="['dp-pill', row.mode === 'percentage' ? 'dp-pill--blue' : 'dp-pill--amber']">
          {{ row.mode === 'percentage' ? 'Percentage' : 'Flat / unit' }}
        </span>
      </template>
      <template #cell-value="{ row }">
        <strong>{{ row.mode === 'percentage' ? `${row.value}%` : `₹${row.value}` }}</strong>
        <small v-if="row.mode === 'flat_per_unit'" class="dp-sub">per medicine</small>
      </template>
      <template #cell-active="{ row }">
        <span :class="['dp-pill', row.active ? 'dp-pill--green' : 'dp-pill--gray']">
          {{ row.active ? 'Active' : 'Inactive' }}
        </span>
      </template>
      <template #cell-actions="{ row }">
        <div class="dp-row-actions">
          <button type="button" class="dp-action-btn dp-action-btn--edit" @click="openEditModal(row)">
            <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
            </svg>
            <span>Edit</span>
          </button>
          <button type="button" class="dp-action-btn dp-action-btn--delete" @click="openDeleteConfirm(row)">
            <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <polyline points="3 6 5 6 21 6"/>
              <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
              <path d="M10 11v6"/>
              <path d="M14 11v6"/>
              <path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2"/>
            </svg>
            <span>Delete</span>
          </button>
        </div>
      </template>
    </BaseTable>

    <CModal
      :show.sync="showModal"
      size="md"
      :centered="true"
      :title="modalTitle"
      class="dp-modal"
      :close-on-backdrop="!isSaving"
    >
      <div class="dp-modal__body">
        <div v-if="modalError" class="dp-modal__alert">{{ modalError }}</div>
        <div class="dp-grid">
          <div class="dp-field dp-field--wide">
            <label class="dp-label">Applies to <span class="dp-req">*</span></label>
            <AppDropdown
              v-model="form.item_id"
              :options="itemDropdownOptions"
              :disabled="isSaving"
              searchable
              search-placeholder="Search products…"
              placeholder="Select scope"
            />
            <small class="dp-hint dp-hint--inline">
              Pick a specific product to override the org-wide tiers for that item only. Product-scoped tiers replace org-wide tiers for the matching line — they don't stack, and the product no longer counts toward the org-wide quantity.
            </small>
          </div>
          <div class="dp-field dp-field--wide">
            <AppInput
              no-margin
              label="Product group (optional)"
              :value="form.group_key"
              placeholder="e.g. farm"
              :disabled="isSaving || !form.item_id"
              @update:value="form.group_key = $event"
            />
            <small class="dp-hint dp-hint--inline">
              <template v-if="!form.item_id">Pick a product above to put it in a group.</template>
              <template v-else>
                Products sharing a group pool their quantities against one shared chart — buying 1 each of two group members counts as 2 toward the tier. Leave blank to make this product's chart stand alone.
                <template v-if="existingGroupKeys.length">
                  Existing: <strong>{{ existingGroupKeys.join(', ') }}</strong>.
                </template>
              </template>
            </small>
          </div>
          <div class="dp-field">
            <AppInput
              no-margin
              label="Min Quantity"
              required
              type="number"
              :value="form.min_quantity"
              :disabled="isSaving"
              @update:value="form.min_quantity = Number($event || 0)"
            />
          </div>
          <div class="dp-field">
            <label class="dp-label">Mode <span class="dp-req">*</span></label>
            <AppDropdown
              v-model="form.mode"
              :options="modeOptions"
              :disabled="isSaving"
            />
          </div>
          <div class="dp-field">
            <AppInput
              no-margin
              :label="form.mode === 'percentage' ? 'Discount %' : 'Discount per unit (₹)'"
              required
              type="number"
              :value="form.value"
              :disabled="isSaving"
              @update:value="form.value = Number($event || 0)"
            />
          </div>
          <div class="dp-field dp-field--wide">
            <AppInput
              no-margin
              label="Label (optional)"
              :value="form.label"
              placeholder="e.g. 6+ medicines"
              :disabled="isSaving"
              @update:value="form.label = $event"
            />
          </div>
          <div class="dp-field dp-field--wide">
            <label class="dp-toggle">
              <input type="checkbox" v-model="form.active" :disabled="isSaving" />
              <span>Active — appears in the OrderForm toggle</span>
            </label>
          </div>
        </div>
      </div>
      <template #footer>
        <button
          type="button"
          class="dp-btn dp-btn--ghost"
          :disabled="isSaving"
          @click="closeModal"
        >Cancel</button>
        <button
          type="button"
          class="dp-btn dp-btn--primary"
          :disabled="isSaving || !isFormValid"
          @click="submitModal"
        >{{ isSaving ? 'Saving…' : 'Save Tier' }}</button>
      </template>
    </CModal>

    <CModal
      :show.sync="showBulkModal"
      size="lg"
      :centered="true"
      title="Bulk apply tiers to products"
      class="dp-modal dp-bulk-modal"
      :close-on-backdrop="!isBulkSaving"
    >
      <div class="dp-modal__body">
        <div v-if="bulkError" class="dp-modal__alert">{{ bulkError }}</div>
        <p class="dp-bulk-intro">
          Define a tier set once and write it across every selected product. Rows that
          already exist for a product (same Min Quantity) are skipped — re-running this is safe.
        </p>

        <div class="dp-bulk-section">
          <div class="dp-bulk-section__head">
            <label class="dp-label dp-label--strong">Apply to products <span class="dp-req">*</span></label>
            <div class="dp-bulk-section__actions">
              <button type="button" class="dp-linklike" :disabled="isBulkSaving" @click="bulkSelectAll(true)">Select all</button>
              <span class="dp-bulk-sep" aria-hidden="true">·</span>
              <button type="button" class="dp-linklike" :disabled="isBulkSaving" @click="bulkSelectAll(false)">Clear</button>
              <span class="dp-bulk-count">{{ bulkSelectedCount }} of {{ items.length }} selected</span>
            </div>
          </div>
          <div class="dp-bulk-products">
            <label
              v-for="it in items"
              :key="it.id"
              class="dp-bulk-product"
              :class="{ 'is-checked': bulkSelectedIds.includes(Number(it.id)) }"
            >
              <input
                type="checkbox"
                :value="Number(it.id)"
                v-model="bulkSelectedIds"
                :disabled="isBulkSaving"
              />
              <span class="dp-bulk-product__name">{{ it.name }}</span>
              <span v-if="it.sku" class="dp-bulk-product__sku">{{ it.sku }}</span>
            </label>
            <div v-if="!items.length" class="dp-bulk-empty">No products in this organisation yet.</div>
          </div>
        </div>

        <div class="dp-bulk-section">
          <div class="dp-bulk-section__head">
            <label class="dp-label dp-label--strong">Product group (optional)</label>
          </div>
          <input
            type="text"
            maxlength="40"
            class="dp-bulk-input dp-bulk-input--group"
            :value="bulkGroupKey"
            placeholder="e.g. farm"
            :disabled="isBulkSaving"
            @input="bulkGroupKey = $event.target.value"
          />
          <small class="dp-hint dp-hint--inline">
            Tag every product above into one group so they pool their quantities against this shared chart — a customer buying 1 each of two members counts as 2 toward the tier. Leave blank to give each product a chart that stands alone.
            <template v-if="existingGroupKeys.length">
              Existing: <strong>{{ existingGroupKeys.join(', ') }}</strong>.
            </template>
          </small>
        </div>

        <div class="dp-bulk-section">
          <div class="dp-bulk-section__head">
            <label class="dp-label dp-label--strong">Tier rows <span class="dp-req">*</span></label>
            <button type="button" class="dp-linklike" :disabled="isBulkSaving" @click="addBulkTier">+ Add tier row</button>
          </div>
          <div class="dp-bulk-tiers">
            <div class="dp-bulk-tiers__head">
              <span>Min Qty</span>
              <span>Mode</span>
              <span>Value</span>
              <span>Label</span>
              <span>Active</span>
              <span aria-hidden="true"></span>
            </div>
            <div v-for="(tier, idx) in bulkTiers" :key="idx" class="dp-bulk-tiers__row">
              <input
                type="number"
                min="1"
                class="dp-bulk-input"
                :value="tier.min_quantity"
                :disabled="isBulkSaving"
                @input="tier.min_quantity = Number($event.target.value || 0)"
              />
              <select
                class="dp-bulk-input"
                v-model="tier.mode"
                :disabled="isBulkSaving"
              >
                <option value="percentage">Percentage</option>
                <option value="flat_per_unit">Flat ₹/unit</option>
              </select>
              <input
                type="number"
                min="0"
                step="0.01"
                class="dp-bulk-input"
                :value="tier.value"
                :disabled="isBulkSaving"
                @input="tier.value = Number($event.target.value || 0)"
              />
              <input
                type="text"
                maxlength="255"
                class="dp-bulk-input"
                :value="tier.label"
                placeholder="e.g. 6+ medicines"
                :disabled="isBulkSaving"
                @input="tier.label = $event.target.value"
              />
              <label class="dp-bulk-tiers__active">
                <input type="checkbox" v-model="tier.active" :disabled="isBulkSaving" />
              </label>
              <button
                type="button"
                class="dp-bulk-tiers__remove"
                :disabled="isBulkSaving || bulkTiers.length <= 1"
                @click="removeBulkTier(idx)"
                aria-label="Remove row"
              >×</button>
            </div>
          </div>
          <small class="dp-hint dp-hint--inline">
            Will create {{ bulkPlanCount }} row{{ bulkPlanCount === 1 ? '' : 's' }} ({{ bulkSelectedCount }} product × {{ bulkTiers.length }} tier). Duplicates are skipped at the server.
          </small>
        </div>
      </div>

      <template #footer>
        <button
          type="button"
          class="dp-btn dp-btn--ghost"
          :disabled="isBulkSaving"
          @click="closeBulkModal"
        >Cancel</button>
        <button
          type="button"
          class="dp-btn dp-btn--primary"
          :disabled="isBulkSaving || !isBulkFormValid"
          @click="submitBulk"
        >{{ isBulkSaving ? 'Applying…' : `Apply to ${bulkSelectedCount} product${bulkSelectedCount === 1 ? '' : 's'}` }}</button>
      </template>
    </CModal>

    <AppConfirmDialog
      :visible="showDeleteConfirm"
      title="Delete this tier?"
      :message="deleteConfirmMessage"
      confirm-label="Delete"
      cancel-label="Keep"
      busy-label="Deleting…"
      :busy="isDeleting"
      variant="danger"
      :close-on-backdrop="!isDeleting"
      @confirm="confirmDelete"
      @cancel="closeDeleteConfirm"
    />
  </div>
</template>

<script>
import axios from 'axios'
import AppDropdown from '@/components/AppDropdown.vue'
import AppInput from '@/components/AppInput.vue'
import BaseTable from '@/components/BaseTable.vue'
import AppConfirmDialog from '@/components/AppConfirmDialog.vue'
import { ORGANISATION_ID_TO_NAME } from '@/views/users/userHelpers.js'

const MODE_OPTIONS = [
  { value: 'percentage', label: 'Percentage off subtotal' },
  { value: 'flat_per_unit', label: 'Flat ₹ per unit' }
]

function blankForm () {
  return {
    id: null,
    organisation_id: null,
    // null = org-wide tier (applies to every product in the org that
    // doesn't have its own override); set = product-scoped override.
    item_id: null,
    // Set (with item_id) = the product joins that group, and its members
    // pool their quantities against one shared ladder.
    group_key: '',
    min_quantity: 1,
    mode: 'percentage',
    value: 0,
    label: '',
    active: true
  }
}

export default {
  name: 'SettingsDiscountPresets',
  components: { AppDropdown, AppInput, BaseTable, AppConfirmDialog },
  data () {
    return {
      selectedOrgId: null,
      presets: [],
      // Items for the selected org, loaded lazily so the modal's
      // Item dropdown only fetches when the admin actually opens it.
      items: [],
      isLoadingItems: false,
      isLoading: false,
      errorMessage: '',
      successMessage: '',
      showModal: false,
      form: blankForm(),
      isSaving: false,
      modalError: '',
      isEdit: false,
      showDeleteConfirm: false,
      presetToDelete: null,
      isDeleting: false,
      modeOptions: MODE_OPTIONS,
      // Bulk-apply state — separate from the single-create form so the
      // user can dismiss one modal without losing the other's draft.
      showBulkModal: false,
      bulkSelectedIds: [],
      bulkGroupKey: '',
      bulkTiers: [],
      isBulkSaving: false,
      bulkError: '',
      columns: [
        { key: 'scope', label: 'Scope', width: '220px' },
        { key: 'group_key', label: 'Group', width: '120px' },
        { key: 'min_quantity', label: 'Min Qty', width: '100px' },
        { key: 'mode', label: 'Mode', width: '140px' },
        { key: 'value', label: 'Value', width: '160px' },
        { key: 'label', label: 'Label' },
        { key: 'active', label: 'Status', width: '110px' },
        { key: 'actions', label: 'Actions', width: '170px', align: 'right' }
      ]
    }
  },
  computed: {
    orgDropdownOptions () {
      return Object.entries(ORGANISATION_ID_TO_NAME).map(([id, name]) => ({
        value: Number(id),
        label: name
      }))
    },
    // "All products" sentinel uses 0 as the AppDropdown value because
    // the component doesn't bind cleanly to null/undefined. The save
    // handler converts 0 → null before POST.
    itemDropdownOptions () {
      return [
        { value: 0, label: 'All products (org-wide)' },
        ...this.items.map((it) => ({ value: Number(it.id), label: it.name }))
      ]
    },
    // Groups already in use for this org — surfaced as a hint so an admin
    // adding a member types the exact same key instead of coining "Farm"
    // next to an existing "farm".
    existingGroupKeys () {
      return Array.from(new Set(
        this.presets.map((p) => (p.group_key || '').trim()).filter(Boolean)
      )).sort()
    },
    itemNameById () {
      const map = {}
      this.items.forEach((it) => { map[Number(it.id)] = it.name })
      return map
    },
    tableRows () {
      // Sort: org-wide tiers first (item_id null), then product-scoped
      // grouped by product, then by min_quantity ascending. Reads as
      // "first the bottle chart, then per-product overrides."
      return this.presets
        .slice()
        .sort((a, b) => {
          const aItem = a.item_id || 0
          const bItem = b.item_id || 0
          if (aItem !== bItem) return aItem - bItem
          return Number(a.min_quantity) - Number(b.min_quantity)
        })
        .map((p) => ({
          ...p,
          label: p.label || '—',
          scope: p.item_id
            ? (this.itemNameById[Number(p.item_id)] || `Item #${p.item_id}`)
            : 'All products'
        }))
    },
    modalTitle () {
      return this.isEdit ? 'Edit Discount Tier' : 'Add Discount Tier'
    },
    isFormValid () {
      const qty = Number(this.form.min_quantity)
      const value = Number(this.form.value)
      return Number.isFinite(qty) && qty >= 1
        && Number.isFinite(value) && value >= 0
        && ['percentage', 'flat_per_unit'].includes(this.form.mode)
    },
    deleteConfirmMessage () {
      const p = this.presetToDelete
      if (!p) return ''
      const valueLabel = p.mode === 'percentage' ? `${p.value}%` : `₹${p.value}/unit`
      return `${p.min_quantity}+ items → ${valueLabel}. Removing it stops the OrderForm toggle from offering this tier.`
    },
    bulkSelectedCount () {
      return this.bulkSelectedIds.length
    },
    bulkPlanCount () {
      return this.bulkSelectedCount * this.bulkTiers.length
    },
    isBulkFormValid () {
      if (!this.bulkSelectedIds.length) return false
      if (!this.bulkTiers.length) return false
      const qtySet = new Set()
      for (const t of this.bulkTiers) {
        const qty = Number(t.min_quantity)
        const val = Number(t.value)
        if (!Number.isFinite(qty) || qty < 1) return false
        if (!Number.isFinite(val) || val < 0) return false
        if (!['percentage', 'flat_per_unit'].includes(t.mode)) return false
        if (qtySet.has(qty)) return false
        qtySet.add(qty)
      }
      return true
    }
  },
  watch: {
    selectedOrgId () {
      this.loadPresets()
      this.loadItems()
    }
  },
  created () {
    // Default to the user's active org so the admin lands on a populated
    // table without an extra click; falls back to org 1 when the store
    // hasn't been hydrated yet (e.g. deep link). The watcher fires both
    // loadPresets() and loadItems() — no explicit call needed here.
    const activeOrg = this.$store && this.$store.state && this.$store.state.activeOrganisationId
    this.selectedOrgId = Number(activeOrg) || 1
  },
  methods: {
    apiBase () {
      return this.$apiAdress
    },
    loadPresets () {
      if (!this.selectedOrgId) return
      this.isLoading = true
      this.errorMessage = ''
      axios.get(`${this.apiBase()}/api/discount-presets`, { params: { organisation_id: this.selectedOrgId } })
        .then((response) => {
          this.presets = Array.isArray(response.data) ? response.data : []
        })
        .catch((error) => {
          this.errorMessage = error?.response?.data?.message || 'Unable to load discount presets.'
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    loadItems () {
      if (!this.selectedOrgId) {
        this.items = []
        return
      }
      this.isLoadingItems = true
      axios.get(`${this.apiBase()}/api/discount-presets/items-by-org/${this.selectedOrgId}`)
        .then((response) => {
          this.items = Array.isArray(response.data) ? response.data : []
        })
        .catch(() => {
          // Soft fail: the modal can still save an org-wide tier
          // (item_id = null) even if the product list didn't load.
          this.items = []
        })
        .finally(() => {
          this.isLoadingItems = false
        })
    },
    openCreateModal () {
      this.form = { ...blankForm(), organisation_id: this.selectedOrgId }
      this.isEdit = false
      this.modalError = ''
      this.showModal = true
    },
    openEditModal (row) {
      this.form = {
        id: row.id,
        organisation_id: row.organisation_id,
        // 0 is the AppDropdown sentinel for "All products" — see
        // itemDropdownOptions. Convert back on submit.
        item_id: row.item_id ? Number(row.item_id) : 0,
        group_key: row.group_key || '',
        min_quantity: Number(row.min_quantity),
        mode: row.mode,
        value: Number(row.value),
        label: row.label === '—' ? '' : (row.label || ''),
        active: Boolean(row.active)
      }
      this.isEdit = true
      this.modalError = ''
      this.showModal = true
    },
    closeModal () {
      if (this.isSaving) return
      this.showModal = false
      this.form = blankForm()
      this.modalError = ''
    },
    submitModal () {
      if (!this.isFormValid) return
      this.isSaving = true
      this.modalError = ''

      const itemId = this.form.item_id ? Number(this.form.item_id) : null
      const payload = {
        organisation_id: this.form.organisation_id || this.selectedOrgId,
        // 0 means "All products" — back-convert to null so the server
        // stores it as an org-wide tier.
        item_id: itemId,
        // A group only means anything alongside a product — an org-wide row
        // is already the catch-all pool.
        group_key: itemId ? (String(this.form.group_key || '').trim() || null) : null,
        min_quantity: Number(this.form.min_quantity),
        mode: this.form.mode,
        value: Number(this.form.value),
        label: this.form.label || null,
        active: Boolean(this.form.active)
      }

      const request = this.isEdit
        ? axios.put(`${this.apiBase()}/api/discount-presets/${this.form.id}`, payload)
        : axios.post(`${this.apiBase()}/api/discount-presets`, payload)

      request
        .then(() => {
          this.successMessage = this.isEdit ? 'Tier updated.' : 'Tier created.'
          setTimeout(() => { this.successMessage = '' }, 2500)
          this.showModal = false
          this.form = blankForm()
          this.loadPresets()
        })
        .catch((error) => {
          this.modalError = error?.response?.data?.message
            || (error?.response?.data?.error && this.firstValidationError(error.response.data.error))
            || 'Failed to save tier.'
        })
        .finally(() => {
          this.isSaving = false
        })
    },
    firstValidationError (errorMap) {
      if (!errorMap || typeof errorMap !== 'object') return ''
      const firstKey = Object.keys(errorMap)[0]
      const list = errorMap[firstKey]
      return Array.isArray(list) ? list[0] : ''
    },
    openDeleteConfirm (row) {
      this.presetToDelete = row
      this.showDeleteConfirm = true
    },
    closeDeleteConfirm () {
      if (this.isDeleting) return
      this.showDeleteConfirm = false
      this.presetToDelete = null
    },
    openBulkModal () {
      if (!this.selectedOrgId) return
      this.bulkSelectedIds = []
      this.bulkGroupKey = ''
      // Seed with a single empty row — gives the user a clear starting
      // shape without prefilling values that might mask typos.
      this.bulkTiers = [this.blankBulkTier()]
      this.bulkError = ''
      this.showBulkModal = true
    },
    closeBulkModal () {
      if (this.isBulkSaving) return
      this.showBulkModal = false
      this.bulkSelectedIds = []
      this.bulkGroupKey = ''
      this.bulkTiers = []
      this.bulkError = ''
    },
    blankBulkTier () {
      return { min_quantity: 1, mode: 'percentage', value: 0, label: '', active: true }
    },
    addBulkTier () {
      this.bulkTiers.push(this.blankBulkTier())
    },
    removeBulkTier (idx) {
      if (this.bulkTiers.length <= 1) return
      this.bulkTiers.splice(idx, 1)
    },
    bulkSelectAll (select) {
      this.bulkSelectedIds = select ? this.items.map((it) => Number(it.id)) : []
    },
    submitBulk () {
      if (!this.isBulkFormValid) return
      this.isBulkSaving = true
      this.bulkError = ''
      const payload = {
        organisation_id: this.selectedOrgId,
        group_key: String(this.bulkGroupKey || '').trim() || null,
        item_ids: this.bulkSelectedIds.map((id) => Number(id)),
        tiers: this.bulkTiers.map((t) => ({
          min_quantity: Number(t.min_quantity),
          mode: t.mode,
          value: Number(t.value),
          label: t.label || null,
          active: Boolean(t.active)
        }))
      }
      axios.post(`${this.apiBase()}/api/discount-presets/bulk`, payload)
        .then((response) => {
          const created = response?.data?.created || 0
          const skipped = (response?.data?.skipped || []).length
          const parts = [`${created} tier${created === 1 ? '' : 's'} created`]
          if (skipped) parts.push(`${skipped} skipped (already existed)`)
          this.successMessage = parts.join(' · ') + '.'
          setTimeout(() => { this.successMessage = '' }, 4000)
          this.showBulkModal = false
          this.bulkSelectedIds = []
          this.bulkTiers = []
          this.loadPresets()
        })
        .catch((error) => {
          this.bulkError = error?.response?.data?.message
            || (error?.response?.data?.error && this.firstValidationError(error.response.data.error))
            || 'Failed to apply tiers.'
        })
        .finally(() => {
          this.isBulkSaving = false
        })
    },
    confirmDelete () {
      if (!this.presetToDelete) return
      this.isDeleting = true
      axios.delete(`${this.apiBase()}/api/discount-presets/${this.presetToDelete.id}`)
        .then(() => {
          this.successMessage = 'Tier deleted.'
          setTimeout(() => { this.successMessage = '' }, 2500)
          this.showDeleteConfirm = false
          this.presetToDelete = null
          this.loadPresets()
        })
        .catch((error) => {
          this.errorMessage = error?.response?.data?.message || 'Failed to delete tier.'
        })
        .finally(() => {
          this.isDeleting = false
        })
    }
  }
}
</script>

<style scoped>
.dp-page { padding: 0.25rem 0; }

.dp-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.25rem 0.25rem 1rem;
  flex-wrap: wrap;
}

.dp-toolbar__title {
  display: flex;
  align-items: center;
  gap: 0.65rem;
}

.dp-toolbar__accent {
  width: 4px;
  height: 36px;
  border-radius: 4px;
  background: linear-gradient(180deg, #0e5b46 0%, #16a07a 100%);
  flex-shrink: 0;
}

.dp-toolbar h1 {
  margin: 0;
  font-size: 1.15rem;
  font-weight: 700;
  color: #1f2a44;
  letter-spacing: -0.01em;
  line-height: 1.2;
}

.dp-toolbar p {
  margin: 0;
  font-size: 0.78rem;
  color: #6b7280;
}

.dp-toolbar__actions {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.dp-toolbar__actions >>> .btn {
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.82rem;
}

.dp-toolbar__actions >>> .btn.refresh-btn {
  height: 34px;
  padding: 0 0.85rem;
  background: #ffffff;
  border: 1px solid #e5e9f2;
  color: #1f2a44;
}

.dp-toolbar__actions >>> .btn.bulk-btn {
  height: 34px;
  padding: 0 0.85rem;
  background: #ffffff;
  border: 1px solid #d8dde6;
  color: #1f2a44;
}

.dp-toolbar__actions >>> .btn.bulk-btn:hover:not(:disabled) {
  border-color: #16a07a;
  color: #0e5b46;
  background: #f5fbf8;
}

.dp-alert { margin: 0 0 0.75rem; border-radius: 10px; }

.dp-section {
  background: #ffffff;
  border: 1px solid #e5e9f2;
  border-radius: 12px;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.04);
  margin-bottom: 1rem;
}

.dp-section__body { padding: 1rem; }

.dp-filters {
  display: flex;
  flex-wrap: wrap;
  gap: 0.85rem;
  align-items: flex-end;
}

.dp-filter {
  display: flex;
  flex-direction: column;
  flex: 1 1 240px;
  min-width: 0;
}

.dp-filter--hint {
  flex: 1 1 280px;
}

.dp-label {
  display: block;
  margin-bottom: 0.3rem;
  font-size: 0.78rem;
  font-weight: 600;
  color: #4f5560;
}

.dp-hint {
  font-size: 0.78rem;
  color: #6b7280;
  line-height: 36px;
}

/* Same colour/size, but used inside the modal under the Applies-to
   dropdown where the 36px baseline-alignment hack would push the
   hint way below the field. Reset to normal multi-line flow. */
.dp-hint--inline {
  line-height: 1.45;
  margin-top: 0.3rem;
  display: block;
}

.dp-hint em {
  font-style: normal;
  color: #0e5b46;
  font-weight: 600;
}

.dp-pill {
  display: inline-flex;
  align-items: center;
  padding: 0.12rem 0.55rem;
  border-radius: 999px;
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: capitalize;
  letter-spacing: 0.02em;
}

.dp-pill--blue   { background: #dbeafe; color: #1e40af; }
.dp-pill--amber  { background: #fef3c7; color: #92400e; }
.dp-pill--green  { background: #dff5ea; color: #0e5b46; }
.dp-pill--gray   { background: #f1f5f9; color: #475569; }
.dp-pill--violet { background: #ede9fe; color: #5b21b6; }

.dp-sub {
  margin-left: 0.4rem;
  font-size: 0.7rem;
  color: #6b7280;
}

.dp-row-actions {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  justify-content: flex-end;
}

.dp-action-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  height: 28px;
  padding: 0 0.7rem;
  border-radius: 6px;
  border: 1px solid #e5e9f2;
  background: #ffffff;
  color: #1f2a44;
  font-size: 0.76rem;
  font-weight: 600;
  cursor: pointer;
  transition: border-color 0.15s ease, color 0.15s ease, background 0.15s ease, box-shadow 0.15s ease;
}

.dp-action-btn--edit:hover {
  border-color: #16a07a;
  color: #0e5b46;
  background: #f5fbf8;
  box-shadow: 0 2px 4px rgba(14, 91, 70, 0.1);
}

.dp-action-btn--delete:hover {
  border-color: #ef4444;
  color: #b91c1c;
  background: #fef2f2;
  box-shadow: 0 2px 4px rgba(239, 68, 68, 0.12);
}

.dp-modal >>> .modal-content {
  border-radius: 14px;
  border: 1px solid #e5e9f2;
  box-shadow: 0 16px 40px rgba(15, 23, 42, 0.22);
  overflow: hidden;
}

.dp-modal >>> .modal-header {
  border-bottom: 1px solid #eef1f7;
  background: #ffffff;
  padding: 0.95rem 1.15rem;
}

.dp-modal >>> .modal-header .modal-title {
  font-size: 1.05rem;
  font-weight: 700;
  color: #1f2a44;
  margin: 0;
  letter-spacing: -0.01em;
}

.dp-modal >>> .modal-body {
  background: #fbfcfe;
  padding: 1.1rem;
  max-height: calc(100vh - 220px);
  overflow-y: auto;
}

.dp-modal >>> .modal-footer {
  border-top: 1px solid #eef1f7;
  background: #ffffff;
  padding: 0.7rem 1.15rem;
  gap: 0.5rem;
}

.dp-modal__body {
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
}

.dp-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 34px;
  min-width: 90px;
  padding: 0.45rem 1.1rem;
  border-radius: 8px;
  font-family: inherit;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease, filter 0.15s ease, background 0.15s ease, color 0.15s ease, border-color 0.15s ease;
}

.dp-btn:disabled {
  cursor: not-allowed;
  opacity: 0.6;
  filter: none;
  transform: none;
  box-shadow: none;
}

.dp-btn--ghost {
  background: #ffffff;
  border: 1px solid #e5e9f2;
  color: #1f2a44;
}

.dp-btn--ghost:hover:not(:disabled) {
  background: #f8fafc;
  border-color: #d8dde6;
}

.dp-btn--primary {
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
  border: 0;
  color: #ffffff;
  box-shadow: 0 2px 6px rgba(14, 91, 70, 0.18);
}

.dp-btn--primary:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 6px 14px rgba(14, 91, 70, 0.28);
  filter: brightness(1.05);
}

.dp-btn--primary:active:not(:disabled) {
  transform: translateY(0);
  filter: brightness(0.96);
}

.dp-modal__alert {
  padding: 0.6rem 0.85rem;
  background: #fee2e2;
  color: #991b1b;
  border: 1px solid #fecaca;
  border-radius: 8px;
  font-size: 0.82rem;
  font-weight: 600;
}

.dp-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.75rem;
}

.dp-field { display: flex; flex-direction: column; min-width: 0; }

.dp-field--wide { grid-column: 1 / -1; }

.dp-req { color: #dc2626; font-weight: 700; }

.dp-toggle {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.85rem;
  color: #1f2a44;
  cursor: pointer;
  margin: 0;
}

@media (max-width: 575px) {
  .dp-grid { grid-template-columns: 1fr; }
}

/* ── Bulk-apply modal ─────────────────────────────────────────── */
.dp-bulk-intro {
  margin: 0;
  font-size: 0.82rem;
  color: #4b5563;
  line-height: 1.45;
}

.dp-bulk-section {
  background: #ffffff;
  border: 1px solid #e5e9f2;
  border-radius: 10px;
  padding: 0.85rem 0.95rem;
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
}

.dp-bulk-section__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.dp-bulk-section__actions {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.78rem;
  color: #6b7280;
}

.dp-bulk-sep { color: #cbd5e1; }

.dp-bulk-count {
  margin-left: 0.4rem;
  font-weight: 600;
  color: #1f2a44;
}

.dp-label--strong {
  margin: 0;
  font-size: 0.82rem;
  color: #1f2a44;
}

.dp-linklike {
  background: none;
  border: 0;
  padding: 0;
  font: inherit;
  color: #0e5b46;
  font-weight: 600;
  cursor: pointer;
}

.dp-linklike:hover:not(:disabled) {
  text-decoration: underline;
}

.dp-linklike:disabled {
  color: #9ca3af;
  cursor: not-allowed;
}

.dp-bulk-products {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 0.4rem;
  max-height: 220px;
  overflow-y: auto;
  padding: 0.5rem;
  background: #fbfcfe;
  border: 1px solid #eef1f7;
  border-radius: 8px;
}

.dp-bulk-product {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.4rem 0.55rem;
  border: 1px solid transparent;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.82rem;
  color: #1f2a44;
  background: #ffffff;
  transition: border-color 0.12s ease, background 0.12s ease;
  min-width: 0;
}

.dp-bulk-product:hover {
  border-color: #d8dde6;
}

.dp-bulk-product.is-checked {
  border-color: #16a07a;
  background: #f5fbf8;
}

.dp-bulk-product input[type='checkbox'] {
  flex: 0 0 auto;
  margin: 0;
  accent-color: #16a07a;
}

.dp-bulk-product__name {
  flex: 1 1 auto;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-weight: 600;
}

.dp-bulk-product__sku {
  flex: 0 0 auto;
  font-size: 0.7rem;
  color: #6b7280;
  font-weight: 500;
}

.dp-bulk-empty {
  grid-column: 1 / -1;
  padding: 0.6rem;
  text-align: center;
  font-size: 0.82rem;
  color: #6b7280;
}

.dp-bulk-tiers {
  display: flex;
  flex-direction: column;
  border: 1px solid #eef1f7;
  border-radius: 8px;
  overflow: hidden;
}

.dp-bulk-tiers__head,
.dp-bulk-tiers__row {
  display: grid;
  grid-template-columns: 90px 130px 110px 1fr 56px 32px;
  align-items: center;
  gap: 0.5rem;
  padding: 0.45rem 0.6rem;
}

.dp-bulk-tiers__head {
  background: #f8fafc;
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #6b7280;
  border-bottom: 1px solid #eef1f7;
}

.dp-bulk-tiers__row + .dp-bulk-tiers__row {
  border-top: 1px solid #f1f5f9;
}

.dp-bulk-input {
  height: 32px;
  padding: 0 0.55rem;
  border: 1px solid #d8dde6;
  border-radius: 6px;
  background: #ffffff;
  font-family: inherit;
  font-size: 0.82rem;
  color: #1f2a44;
  width: 100%;
  min-width: 0;
}

/* Sits on its own row rather than in the tier grid, so cap the width
   instead of letting it stretch the full modal. */
.dp-bulk-input--group {
  max-width: 240px;
  margin-bottom: 0.4rem;
}

.dp-bulk-input:focus {
  outline: none;
  border-color: #16a07a;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.18);
}

.dp-bulk-tiers__active {
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0;
}

.dp-bulk-tiers__active input { accent-color: #16a07a; margin: 0; }

.dp-bulk-tiers__remove {
  width: 26px;
  height: 26px;
  border-radius: 6px;
  border: 1px solid #e5e9f2;
  background: #ffffff;
  color: #6b7280;
  font-size: 1.05rem;
  line-height: 1;
  cursor: pointer;
  transition: border-color 0.15s ease, color 0.15s ease, background 0.15s ease;
}

.dp-bulk-tiers__remove:hover:not(:disabled) {
  border-color: #ef4444;
  color: #b91c1c;
  background: #fef2f2;
}

.dp-bulk-tiers__remove:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

@media (max-width: 640px) {
  .dp-bulk-tiers__head { display: none; }
  .dp-bulk-tiers__row {
    grid-template-columns: 1fr 1fr;
    gap: 0.4rem;
    padding: 0.55rem 0.6rem;
  }
}

@media (max-width: 575px) {
  .dp-page { padding: 0 0 0.5rem; }
  .dp-toolbar {
    flex-wrap: wrap;
    padding: 0.25rem 0 0.75rem;
    gap: 0.5rem;
    align-items: flex-start;
  }
  .dp-toolbar__title {
    flex: 1 1 100%;
  }
  .dp-toolbar__accent {
    height: 30px;
  }
  .dp-toolbar h1 {
    font-size: 1rem;
  }
  .dp-toolbar p {
    font-size: 0.72rem;
  }
  .dp-toolbar__actions {
    flex: 1 1 100%;
    flex-wrap: wrap;
    gap: 0.4rem;
  }
  .dp-toolbar__actions >>> .btn {
    height: 32px;
    font-size: 0.76rem;
    padding: 0 0.7rem;
  }
  .dp-row-actions {
    flex-wrap: wrap;
    gap: 0.3rem;
  }
}
</style>
